import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import moment from 'moment/moment';
import { FinancialStatementVersionValidator, MeasurementDate, SheetAttributeMapping } from 'api';
import { MAX_HISTORICAL_YEARS } from 'common/config/app';
import { FINANCIAL_TITLE } from 'common/constants/financials';
import { useCompanyBreadcrumb } from 'common/hooks';
import { UseCompanyBreadcrumbParams } from 'common/hooks/useCompanyBreadcrumb/types';
import { FinancialsPerformanceMetricsColumn } from 'common/types/financials';
import { ConfirmationDeleteWithList, ConfirmationDialog } from 'components/Dialogs';
import { DialogContext } from 'context';
import { useFinancials } from 'context/FinancialsContext';
import LayoutContext from 'context/LayoutContext';
import { IWorkbook } from 'models/interfaces/spreadsheet-types/workbook-types';
import bsReverseParser from 'pages/Financials/balance-sheet/utilities/reverseParser';
import addFinancialYear from 'pages/Financials/utilities/addFinancialYear';
import getFinancialsData from 'pages/Financials/utilities/getFinancialsData';
import saveFinancialStatement from 'pages/Financials/utilities/saveFinancialStatement';
import updateOrCreateFinancialStatement from 'pages/Financials/utilities/updateOrCreateFinancialStatement';
import { useGetAllocationStatusBreadcrumb } from 'services/hooks/allocation';
import { useCreateFinancialStatement, useUpdateFinancialStatement } from 'services/hooks/financialStatement';
import { getStringValue, toString } from 'utilities';
import {
  FinancialPeriodColumn,
  FinancialTableData,
  IncomeStatementColumn,
  MeasurementDateParams,
  UseFinancialPageHelperProps,
} from './types';
import { useAttributeMapperStore } from '../../../store/useAttributeMapperStore';
import convertCamelCaseToSnakeCase from '../../../utilities/convertCamelCaseToSnakeCase/convertCamelCaseToSnakeCase';
import { BalanceSheetCell } from '../balance-sheet/types';
import ConfirmationFinal from '../components/confirmation-final/ConfirmationFinal';
import { KpiReverseParserType } from '../kpi/config/types';

// this hook is used to render UI related to the financials page, but does not load any data or make any API calls
const useFinancialsPageHelpers = ({
  financialStatement,
  setFinancialStatement,
  tableData,
  periods,
  companyMeasurementDate,
  getVersions,
  measurementDates,
  measurementDate,
  filesParams,
  notesParams,
  createVersion,
  showAdditionalHistoricalYears,
  setShowAdditionalHistoricalYears,
  additionalHistoricalYears,
  setAdditionalHistoricalYears,
  selectedVersion,
  isDisabled,
  isAllocationFinal,
  selectedDate,
}: UseFinancialPageHelperProps) => {
  const [isNewVersionDialogOpen, setIsNewVersionDialogOpen] = useState(false);
  const [isNameDialogOpen, setIsNameDialogOpen] = useState(false);
  const { updatePageActions } = useContext(LayoutContext);
  const [create] = useCreateFinancialStatement();
  const [update] = useUpdateFinancialStatement();
  const [addingNewYear, setAddingNewYear] = useState(false);
  const { showDialog } = useContext(DialogContext);
  const { useAdjustedEBITDA } = useFinancials();
  const { getAttributeMapperManager } = useAttributeMapperStore();
  const { allocationStatusBreadcrumb } = useGetAllocationStatusBreadcrumb();

  const { icon: status, title: label } = allocationStatusBreadcrumb;

  const financialsBreadcrumb = useMemo(
    () => ({
      page: FINANCIAL_TITLE,
      item: selectedVersion
        ? {
          label: getStringValue(selectedVersion?.name),
          allocationVersion: {
            label,
            status,
          },
        }
        : {},
      customAction: () => setIsNameDialogOpen(true),
    }),
    [label, selectedVersion, status]
  ) as UseCompanyBreadcrumbParams;

  useCompanyBreadcrumb(financialsBreadcrumb);

  useEffect(() => {
    if (addingNewYear && !showAdditionalHistoricalYears && !isEmpty(additionalHistoricalYears)) {
      setShowAdditionalHistoricalYears(true);
      setAddingNewYear(false);
    }
  }, [addingNewYear, additionalHistoricalYears, showAdditionalHistoricalYears, setShowAdditionalHistoricalYears]);

  const getUpdatedTableData = useCallback(
    (showAdditionalHistoricalYearValue: boolean) => {
      if (tableData) {
        const additionalHistoricalCallback = (item: FinancialsPerformanceMetricsColumn | BalanceSheetCell) => {
          if ((item as FinancialsPerformanceMetricsColumn).isAdditionalHistoricalYear) {
            return {
              ...item,
              showAdditionalHistoricalYear: showAdditionalHistoricalYearValue,
            };
          }
          return item;
        };
        const incomeStatement = tableData?.incomeStatement?.reverseParse().map(additionalHistoricalCallback);
        const performanceMetrics = tableData?.performanceMetrics?.columns.map(additionalHistoricalCallback);
        const balanceSheet = bsReverseParser(tableData?.balanceSheet).map(additionalHistoricalCallback);
        tableData.incomeStatement.reset({ columns: incomeStatement });
        tableData.performanceMetrics.reset({ columns: performanceMetrics });
        tableData.balanceSheet.reset({ columns: balanceSheet });

        if (!isEmpty(tableData.KPI)) {
          const kpi = tableData.KPI.reverseParse().map(additionalHistoricalCallback);
          tableData.KPI.reset({ columns: kpi } as KpiReverseParserType);
        }
        return tableData;
      }
    },
    [tableData]
  );

  const showOrHideEarliestHistoricalYears = useCallback(
    async (shouldEvaluateSpreadsheet = true) => {
      if (tableData) {
        const shouldShowAdditionalHistoricalYears = !showAdditionalHistoricalYears;
        setShowAdditionalHistoricalYears(shouldShowAdditionalHistoricalYears);
        const updatedTableData = getUpdatedTableData(shouldShowAdditionalHistoricalYears)!;
        if (!shouldEvaluateSpreadsheet) {
          return updatedTableData;
        }

        const sheetsToEvaluate = [
          updatedTableData.incomeStatement,
          updatedTableData.balanceSheet,
          updatedTableData.performanceMetrics,
        ];
        if (!isEmpty(updatedTableData.KPI)) {
          sheetsToEvaluate.push(updatedTableData.KPI);
        }
        await tableData.evaluateSpreadsheets(sheetsToEvaluate);
      }
    },
    [getUpdatedTableData, setShowAdditionalHistoricalYears, showAdditionalHistoricalYears, tableData]
  );

  const scrollToEndOfTables = useCallback((isPrevYear: boolean) => {
    const financialTables = document.querySelectorAll('#financials .scrollbar-container');

    if (isPrevYear) {
      Array.from(financialTables).forEach(item => {
        // eslint-disable-next-line no-param-reassign
        item.scrollLeft = 0;
      });
    } else {
      Array.from(financialTables).forEach(item => {
        // eslint-disable-next-line no-param-reassign
        item.scrollLeft = item.scrollWidth;
      });
    }
  }, []);

  const addYearFn = useCallback(
    async ({ isPrevYear }: { isPrevYear: boolean }) => {
      if (!periods || !tableData || !measurementDate || !additionalHistoricalYears) {
        return;
      }

      let updatedTableData = tableData;
      if (!showAdditionalHistoricalYears && !isEmpty(additionalHistoricalYears)) {
        updatedTableData = (await showOrHideEarliestHistoricalYears(false))!;
      }
      const currentPeriod = moment(measurementDate?.date);
      const currentPeriodYear = currentPeriod.year();
      const defaultLimitYear = currentPeriodYear - MAX_HISTORICAL_YEARS;
      await addFinancialYear({
        tableData: updatedTableData,
        financialStatement: financialStatement!,
        periods: periods as FinancialPeriodColumn[],
        scrollToEndOfTables,
        defaultLimitYear,
        additionalHistoricalYears,
        setAdditionalHistoricalYears,
        isPrevYear,
        measurementDate,
      });
      setAddingNewYear(true);
    },
    [
      additionalHistoricalYears,
      financialStatement,
      periods,
      scrollToEndOfTables,
      measurementDate,
      setAdditionalHistoricalYears,
      showOrHideEarliestHistoricalYears,
      tableData,
      showAdditionalHistoricalYears,
    ]
  );

  /* periodIndex/colIndex is not reliable as an indicator of where to delete
  (periods remain untouched, but colIndex doesn't) */
  const deleteColumn = useCallback(
    (columnIndex: number, periodId: number | string, wb: IWorkbook) => {
      if (!periods || !additionalHistoricalYears) {
        return;
      }
      /*
    - if fs is saved periodId is number string and period.id is integer, and we need casting
    */
      const tmpPeriods = [...periods];
      const yearToDelete = tmpPeriods.find(period => toString(period.id) === toString(periodId))!;
      yearToDelete.is_deleted = true;

      wb.incomeStatement.deleteColumns(columnIndex, 1);
      wb.balanceSheet.deleteColumns(columnIndex, 1);
      wb.performanceMetrics.deleteColumns(columnIndex, 1);
      const spreadsheetsToEvaluate = [wb.incomeStatement, wb.balanceSheet, wb.performanceMetrics];

      if (!isEmpty(wb.KPI)) {
        wb.KPI.deleteColumns(columnIndex, 1);
        spreadsheetsToEvaluate.push(wb.KPI);
      }

      wb.evaluateSpreadsheets(spreadsheetsToEvaluate);

      const currentPeriodId = Number.isNaN(Number(periodId)) ? periodId : Number(periodId);
      const updatedAdditionalHistoricalYears = additionalHistoricalYears.filter(year => year.id !== currentPeriodId);
      setAdditionalHistoricalYears(updatedAdditionalHistoricalYears);
    },
    [additionalHistoricalYears, periods, setAdditionalHistoricalYears]
  );

  const handleDeleteColumn = (columnIndex: number, wb: FinancialTableData) => {
    const firstValue = 0;
    // last two columns are NTM and LTM so
    const lastValue = wb.incomeStatement.columns.length - 2 - 1;
    return columnIndex === firstValue || columnIndex === lastValue;
  };

  const PROJECTION_YEAR_LIMIT = 2;
  const HISTORICAL_YEAR_LIMIT = 1;

  const checkProjectionYearLimit = (columnIndex: number, wb: FinancialTableData) => {
    if (wb.incomeStatement.columns[columnIndex].isProjectedYear) {
      return (
        wb.incomeStatement.columns.filter((column: IncomeStatementColumn) => column.isProjectedYear).length <=
        PROJECTION_YEAR_LIMIT
      );
    }
    return false;
  };

  const checkHistoricalYearLimit = (columnIndex: number, wb: FinancialTableData) => {
    if (wb.incomeStatement.columns[columnIndex].isPrevYear) {
      return (
        wb.incomeStatement.columns.filter((column: IncomeStatementColumn) => column.isPrevYear).length <=
        HISTORICAL_YEAR_LIMIT
      );
    }
    return false;
  };

  const showDeleteConfirmation = useCallback(
    (columnIndex: number, columnPeriodId: string, workbook: FinancialTableData) => {
      const isOutsideProjectionYearLimit = checkProjectionYearLimit(columnIndex, workbook);
      const isOutsideHistoricalYearLimit = checkHistoricalYearLimit(columnIndex, workbook);
      const CAN_DELETE =
        isOutsideProjectionYearLimit || isOutsideHistoricalYearLimit
          ? false
          : handleDeleteColumn(columnIndex, workbook);
      const notAllowedToDeleteMessage =
        isOutsideProjectionYearLimit || isOutsideHistoricalYearLimit
          ? [
            'At least two projection years are required in order to sync values with the NTM column, and at least one historical year is required to sync with the LTM column.',
          ]
          : ['This column cannot be deleted because it affects the sequence of the following years.'];

      const CAN_DELETE_MESSAGE = [
        'Income statement for the fiscal year',
        'Balance sheet for the fiscal year',
        'The performance metrics relative to this period',
      ];
      if (!isEmpty(workbook.KPI)) {
        CAN_DELETE_MESSAGE.push('The KPIs for the fiscal year');
      }
      const messages = CAN_DELETE ? CAN_DELETE_MESSAGE : notAllowedToDeleteMessage;
      const notDeleteMessage = ['You are not be able to delete this column'];
      const dialogTitle = CAN_DELETE ? 'Are you sure you want to delete this column?' : 'You cannot delete this column';
      const dialogBtnLabel = CAN_DELETE ? 'Delete' : 'Done';
      const dialogBtnType = CAN_DELETE ? 'danger' : 'success';
      const dialogBtnAction = CAN_DELETE ? () => deleteColumn(columnIndex, columnPeriodId, workbook) : () => {};
      const name = 'Delete column';

      showDialog({
        wrapper: ConfirmationDialog,
        wrapperProps: {
          title: dialogTitle,
        },
        content: ConfirmationDeleteWithList,
        contentProps: { messages, name, notDeleteMessage },
        actions: [
          {
            label: dialogBtnLabel,
            variant: 'contained',
            type: dialogBtnType,
            callback: dialogBtnAction,
          },
        ],
      });
    },
    [deleteColumn, showDialog]
  );

  const measurementDateParams = useMemo(
    () => ({
      companyMeasurementDate,
      selectedMeasurementDate: measurementDate,
      measurementDates,
    }),
    [companyMeasurementDate, measurementDates, measurementDate]
  );

  const saveConfirmation = useCallback(
    callback => {
      if (isAllocationFinal) {
        showDialog({
          wrapper: ConfirmationDialog,
          wrapperProps: {
            title: 'Confirmation',
          },
          content: ConfirmationFinal,
          actions: [
            {
              label: 'Cancel',
              variant: 'text',
              type: 'primary',
            },
            {
              label: 'Save',
              variant: 'contained',
              type: 'success',
              callback,
            },
          ],
        });
      }
    },
    [isAllocationFinal, showDialog]
  );

  const saveFn = useCallback(async () => {
    if (!periods || !financialStatement) {
      return;
    }

    const attributeMapperManager = getAttributeMapperManager()
    const attributeMappingManagerData: SheetAttributeMapping = attributeMapperManager
      ? convertCamelCaseToSnakeCase(attributeMapperManager?.sheetAttributeMapping)
      : {};

    if (isAllocationFinal) {
      saveConfirmation(() =>
        saveFinancialStatement(
          tableData,
          periods as FinancialPeriodColumn[],
          { ...financialStatement, sheet_attribute_mapping: attributeMappingManagerData },
          create,
          getVersions as any,
          update,
          setFinancialStatement as any,
          { ...measurementDateParams } as unknown as MeasurementDateParams,
          { ...filesParams },
          undefined, // terrible... but we need to pass notesParams to saveFinancialStatement
          showAdditionalHistoricalYears,
          useAdjustedEBITDA,
          selectedDate
        )
      );
    } else {
      saveFinancialStatement(
        tableData,
        periods as FinancialPeriodColumn[],
        { ...financialStatement, sheet_attribute_mapping: attributeMappingManagerData },
        create,
        getVersions as any,
        update,
        setFinancialStatement as any,
        { ...measurementDateParams } as unknown as MeasurementDateParams,
        { ...filesParams },
        { ...notesParams },
        showAdditionalHistoricalYears,
        useAdjustedEBITDA,
        selectedDate
      );
    }
  }, [periods, financialStatement, getAttributeMapperManager, isAllocationFinal, saveConfirmation, tableData, create, getVersions, update, setFinancialStatement, measurementDateParams, filesParams, showAdditionalHistoricalYears, useAdjustedEBITDA, selectedDate, notesParams]);

  // Set page actions
  useEffect(() => {
    if (!isEmpty(financialStatement)) {
      const isActive = financialStatement?.id && !financialStatement?.versioning;
      updatePageActions?.({
        mainAction: {
          id: 'financials',
          label: 'Save',
          callback: saveFn,
          isActive: isDisabled,
          isLock: isAllocationFinal,
        },
        secondaryActions: [
          {
            label: 'Save as New Version',
            callback: () => setIsNewVersionDialogOpen(true),
            isActive,
          },
        ],
      });
    }
    return () => updatePageActions?.(null);
  }, [financialStatement, updatePageActions, saveFn, isDisabled, isAllocationFinal]);

  const saveStatementWithNewName = useCallback(
    (newName: string) => {
      if (!periods || !financialStatement) {
        return;
      }

      const financialStamentData = getFinancialsData(
        tableData?.incomeStatement,
        tableData?.balanceSheet,
        tableData?.KPI,
        financialStatement as any,
        periods as FinancialPeriodColumn[],
        measurementDate as MeasurementDate
      );
      if (newName && newName !== financialStamentData.name) {
        const updatedFinancialStatementData = { ...financialStamentData, name: newName };
        updateOrCreateFinancialStatement({
          financialStatementData: updatedFinancialStatementData,
          create,
          update,
          getVersions: getVersions as any,
        });
      }
    },
    [create, financialStatement, getVersions, periods, tableData, update, measurementDate]
  );

  const saveAsNewVersion = useCallback(
    values => {
      if (!periods || !financialStatement || !companyMeasurementDate) {
        return;
      }

      const financialStatementData = getFinancialsData(
        tableData?.incomeStatement,
        tableData?.balanceSheet,
        tableData?.KPI,
        financialStatement as any,
        periods as FinancialPeriodColumn[],
        measurementDate as MeasurementDate
      );
      financialStatementData.name = values?.name;
      financialStatementData.company_measurement_date = companyMeasurementDate?.id;
      financialStatementData.show_additional_historical_years = showAdditionalHistoricalYears;
      financialStatementData.company_kpis = tableData?.KPI.tableData.companyKPIs;
      financialStatementData.deleted_kpis = tableData?.KPI.tableData.deletedKPIs || [];

      createVersion(financialStatement.id!, financialStatementData as FinancialStatementVersionValidator);
    },
    [
      companyMeasurementDate,
      createVersion,
      financialStatement,
      periods,
      showAdditionalHistoricalYears,
      tableData,
      measurementDate,
    ]
  );
  return {
    setOpenNewVersionDialog: setIsNewVersionDialogOpen,
    isNewVersionDialogOpen,
    setIsNameDialogOpen,
    isNameDialogOpen,
    saveStatementWithNewName,
    saveAsNewVersion,
    addFinancialYear: addYearFn,
    showDeleteConfirmation,
    showOrHideEarliestHistoricalYears,
  };
};

export default useFinancialsPageHelpers;
